import { Check, X, Loader2 } from 'lucide-react';
import { DiffSection, ResponsesDiff } from './DiffBlocks';

/**
 * Generic diff panel used for Quick Edit, AutoImprove and "Améliorer".
 *
 * Props:
 *  - title: string shown in the header
 *  - icon: optional ReactNode rendered before the title
 *  - accent: optional CSS color for the header tint (defaults to neutral)
 *  - items: Array<{
 *      id: string | number,
 *      label?: string,           // short preview / sujet label
 *      before: { enonce, reponses, explication, bonne_reponse },
 *      after:  { enonce, reponses, explication, bonne_reponse? }
 *    }>
 *  - onChangeAfter(id, field, value) — update after.enonce / after.reponses / after.explication
 *  - onSaveOne(id) — async, apply one item
 *  - onDismissOne(id) — remove one item from the list
 *  - onSaveAll() — async, apply all
 *  - onClose() — dismiss the whole panel
 *  - extraItemActions(item) — optional render prop for extra per-item buttons
 *  - busyId — id currently being saved (shows spinner)
 */
export default function QuickEditDiffPanel({
    title,
    icon,
    accent,
    items,
    onChangeAfter,
    onSaveOne,
    onDismissOne,
    onSaveAll,
    onClose,
    extraItemActions,
    busyId,
}) {
    if (!items || items.length === 0) return null;

    const headerStyle = accent
        ? { borderColor: accent }
        : undefined;
    const headerBarStyle = accent
        ? { background: `color-mix(in srgb, ${accent} 8%, transparent)`, color: accent }
        : undefined;

    return (
        <div className="aq-bulk-diff" style={headerStyle}>
            <div className="aq-bulk-diff-header" style={headerBarStyle}>
                {icon}
                <span>{title} — {items.length} question{items.length > 1 ? 's' : ''}</span>
                {onSaveAll && (
                    <button className="btn btn-xs btn-primary" onClick={onSaveAll}>
                        <Check size={12} /> Tout appliquer
                    </button>
                )}
                {onClose && (
                    <button className="btn btn-xs btn-ghost" onClick={onClose}>
                        <X size={12} /> Fermer
                    </button>
                )}
            </div>
            <div className="aq-bulk-diff-list">
                {items.map((item) => {
                    const before = item.before || {};
                    const after = item.after || {};
                    const preview = (item.label
                        || (Array.isArray(before.enonce) && before.enonce.find(b => b.type === 'paragraph')?.content?.replace(/<[^>]+>/g, '').slice(0, 80))
                        || '—');
                    const isBusy = busyId === item.id;
                    return (
                        <div key={item.id} className="aq-bulk-diff-item">
                            <div className="aq-bulk-diff-item-header">
                                <span className="aq-bulk-diff-item-id">#{item.id}</span>
                                <span className="aq-bulk-diff-item-preview">{preview}</span>
                                <div className="aq-bulk-diff-item-actions">
                                    {onSaveOne && (
                                        <button
                                            className="btn btn-xs btn-primary"
                                            onClick={() => onSaveOne(item.id)}
                                            disabled={isBusy}
                                        >
                                            {isBusy ? <Loader2 size={12} className="spin" /> : <Check size={12} />} Appliquer
                                        </button>
                                    )}
                                    {extraItemActions && extraItemActions(item)}
                                    {onDismissOne && (
                                        <button className="btn btn-xs btn-ghost" onClick={() => onDismissOne(item.id)}>
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="aq-bulk-diff-item-body">
                                <DiffSection
                                    label="Énoncé"
                                    before={before.enonce}
                                    after={after.enonce}
                                    onChangeAfter={(v) => onChangeAfter?.(item.id, 'enonce', v)}
                                />
                                <ResponsesDiff
                                    before={before.reponses}
                                    after={after.reponses}
                                    bonneReponse={before.bonne_reponse}
                                    newBonneReponse={after.bonne_reponse}
                                    onChangeAfter={(v) => onChangeAfter?.(item.id, 'reponses', v)}
                                />
                                <DiffSection
                                    label="Explication"
                                    before={before.explication}
                                    after={after.explication}
                                    onChangeAfter={(v) => onChangeAfter?.(item.id, 'explication', v)}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
